import { useState, useCallback, useEffect, useMemo } from 'react';
import type {
  Command,
  CommandContext,
  UseCommandPaletteReturn,
  CommandPaletteKeyboardConfig,
  CommandPaletteConfig,
} from '../types/command.types';
import { CommandRegistryService } from '../services/CommandRegistryService';

interface UseCommandPaletteProps {
  /** Command context */
  context: CommandContext;
  /** Initial commands to register */
  commands?: Command[];
  /** Keyboard configuration */
  keyboard?: CommandPaletteKeyboardConfig;
  /** Command palette configuration */
  config?: CommandPaletteConfig;
  /** Command registry service (optional, uses global if not provided) */
  registry?: CommandRegistryService;
}

/**
 * Hook for managing command palette state and behavior
 * Handles command registration, filtering, keyboard navigation, and execution
 */
export function useCommandPalette({
  context,
  commands: initialCommands = [],
  keyboard = { key: ' ', altKey: true },
  config = {},
  registry: providedRegistry,
}: UseCommandPaletteProps): UseCommandPaletteReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [registeredCommands, setRegisteredCommands] = useState<Command[]>([]);

  // Use provided registry or create a new one
  const registry = useMemo(
    () => providedRegistry || new CommandRegistryService(),
    [providedRegistry]
  );

  // Create command context with close function (before effects)
  const commandContext = useMemo<CommandContext>(
    () => ({
      ...context,
      closeCommandPalette: () => setIsOpen(false),
    }),
    [context]
  );

  // Register initial commands and subscribe to changes
  useEffect(() => {
    let isSubscribed = true;

    // Subscribe to changes first
    const unsubscribe = registry.subscribe(() => {
      if (isSubscribed) {
        const updatedCommands = registry.getCommands(commandContext);
        setRegisteredCommands(updatedCommands);
      }
    });

    // Register commands after subscribing (this will trigger the subscription)
    if (initialCommands.length > 0) {
      registry.registerCommands(initialCommands);
    } else {
      // If no initial commands, still need to get current commands
      const commands = registry.getCommands(commandContext);
      setRegisteredCommands(commands);
    }

    return () => {
      isSubscribed = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registry]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (config.fuzzySearch === false) {
      // Simple substring matching
      return registry.searchCommands(query, commandContext);
    }

    // Use registry's built-in search
    return registry.searchCommands(query, commandContext);
  }, [query, registry, commandContext, config.fuzzySearch]);

  // Reset selected index when filtered commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  // Open command palette
  const open = useCallback(() => {
    setIsOpen(true);
    setQuery('');
    setSelectedIndex(0);

    // Emit event
    context.events?.emit({
      type: 'command-palette:opened',
      source: 'command-palette',
      timestamp: Date.now(),
      payload: {},
    });
  }, [context.events]);

  // Close command palette
  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);

    // Emit event
    context.events?.emit({
      type: 'command-palette:closed',
      source: 'command-palette',
      timestamp: Date.now(),
      payload: {},
    });
  }, [context.events]);

  // Toggle command palette
  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Execute a command by ID
  const executeCommand = useCallback(
    async (commandId: string) => {
      await registry.executeCommand(commandId, commandContext);
      close();
    },
    [registry, commandContext, close]
  );

  // Navigate to next command
  const selectNext = useCallback(() => {
    setSelectedIndex((prev) => {
      const maxResults = config.maxResults || filteredCommands.length;
      const maxIndex = Math.min(filteredCommands.length, maxResults) - 1;
      return prev < maxIndex ? prev + 1 : 0;
    });
  }, [filteredCommands.length, config.maxResults]);

  // Navigate to previous command
  const selectPrevious = useCallback(() => {
    setSelectedIndex((prev) => {
      const maxResults = config.maxResults || filteredCommands.length;
      const maxIndex = Math.min(filteredCommands.length, maxResults) - 1;
      return prev > 0 ? prev - 1 : maxIndex;
    });
  }, [filteredCommands.length, config.maxResults]);

  // Execute the currently selected command (always first result)
  const executeSelected = useCallback(async () => {
    // Always execute the first filtered command
    const firstCommand = filteredCommands[0];

    if (firstCommand) {
      await executeCommand(firstCommand.id);
    }
  }, [filteredCommands, executeCommand]);

  // Register new commands
  const registerCommands = useCallback(
    (commands: Command[]) => {
      registry.registerCommands(commands);
    },
    [registry]
  );

  // Unregister commands
  const unregisterCommands = useCallback(
    (commandIds: string[]) => {
      registry.unregisterCommands(commandIds);
    },
    [registry]
  );

  // Handle keyboard shortcut to open command palette
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const {
        key = ' ',
        altKey = true,
        ctrlKey = false,
        metaKey = false,
        shiftKey = false,
      } = keyboard;

      // Check if the keyboard shortcut matches
      const modifiersMatch =
        e.altKey === altKey &&
        e.ctrlKey === ctrlKey &&
        e.metaKey === metaKey &&
        e.shiftKey === shiftKey;

      // Use event.code for Space key to match usePanelKeyboardShortcuts pattern
      const keyMatches = key === ' ' ? e.code === 'Space' : e.key === key;

      if (keyMatches && modifiersMatch) {
        // Don't trigger if user is typing in an input field
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }

        e.preventDefault();
        toggle();
      }
    },
    [keyboard, toggle]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    isOpen,
    open,
    close,
    toggle,
    query,
    setQuery,
    filteredCommands,
    executeCommand,
    selectedIndex,
    setSelectedIndex,
    selectNext,
    selectPrevious,
    executeSelected,
    commands: registeredCommands,
    registerCommands,
    unregisterCommands,
  };
}
